const { getElement, debounce } = window.BAO.Utils

class RangeSlider extends window.BAO.CustomElement() {
  static get requiredAttributes () {
    return ['min', 'max']
  }

  static get requiredElements () {
    return ['touchLeft', 'touchRight', 'line']
  }

  constructor () {
    super()

    this.type = 'shopify'

    if (window.theme.category || window.theme.searchResults) {
      this.type = 'klevu'

      this.state = {
        root: window.theme.category
          ? window.theme.category.stateLayer.state
          : window.theme.searchResults.stateLayer.state,
      }

      this.eventBus = window.theme.category
        ? window.theme.category.eventBus
        : window.theme.searchResults.eventBus
    }
  }

  connectedCallback () {
    super.connectedCallback()

    this.startX = 0
    this.x = 0
    this.selectedTouchEl = null

    this.setTouchElPositions()
  }

  setupListeners () {
    super.setupListeners()

    this.onStart = this.onStart.bind(this)
    this.onStop = this.onStop.bind(this)
    this.onMove = this.onMove.bind(this)
    this.setTouchElPositions = this.setTouchElPositions.bind(this)

    this.listeners.add(this.touchLeftEl, 'mousedown', this.onStart)
    this.listeners.add(this.touchRightEl, 'mousedown', this.onStart)
    this.listeners.add(this.touchLeftEl, 'touchstart', this.onStart)
    this.listeners.add(this.touchRightEl, 'touchstart', this.onStart)

    this.onResize = this.onResize.bind(this)
    this.debouncedResize = debounce(event => {
      this.onResize(event)
    }, 240)
    this.listeners.add(window, 'resize', this.debouncedResize)

    const detailsEl = this.closest('details')
    if (detailsEl) {
      detailsEl.addEventListener('toggle', event => {
        if (event.target.open) {
          setTimeout(this.setTouchElPositions, 100)
        }
      })
    }

    if (this.type === 'klevu') {
      this.eventBus.subscribe('state:resetPriceRange', () => {
        this.minValue = this.min
        this.maxValue = this.max
        this.setTouchElPositions()
      })

      this.eventBus.subscribe('price-range:change', ({ min, max }) => {
        this.minValue = min
        this.maxValue = max
        this.setTouchElPositions()
      })
    }
  }

  onResize () {
    this.setTouchElPositions()
  }

  onStart (event) {
    if (event?.button && event?.button !== 0) return

    // Prevent default dragging of selected content
    event.preventDefault()
    this.eventTouch = event.touches ? event.touches[0] : event

    this.selectedTouchEl =
      event.target.closest(this.els.touchLeft.selector) ||
      event.target.closest(this.els.touchRight.selector)

    this.x = this.selectedTouchEl.matches(this.els.touchLeft.selector)
      ? this.touchLeftEl.offsetLeft
      : this.touchRightEl.offsetLeft
    this.startX = this.eventTouch.pageX - this.x

    document.addEventListener('mousemove', this.onMove)
    document.addEventListener('mouseup', this.onStop)
    document.addEventListener('touchmove', this.onMove)
    document.addEventListener('touchend', this.onStop)
  }

  onStop () {
    document.removeEventListener('mousemove', this.onMove)
    document.removeEventListener('mouseup', this.onStop)
    document.removeEventListener('touchmove', this.onMove)
    document.removeEventListener('touchend', this.onStop)

    const changeEvent = new Event('change', {
      bubbles: true,
      cancelable: true,
    })

    if (
      this.minToUpdateEl &&
      this.selectedTouchEl.matches(this.els.touchLeft.selector)
    ) {
      this.minToUpdateEl.dispatchEvent(changeEvent)
    }

    if (
      this.maxToUpdateEl &&
      this.selectedTouchEl.matches(this.els.touchRight.selector)
    ) {
      this.maxToUpdateEl.dispatchEvent(changeEvent)
    }

    this.selectedTouchEl = null
  }

  onMove (event) {
    this.eventTouch = event.touches ? event.touches[0] : event

    this.x = this.eventTouch.pageX - this.startX

    if (this.selectedTouchEl.matches(this.els.touchLeft.selector)) {
      if (this.x > this.touchRightEl.offsetLeft) {
        this.x = this.touchRightEl.offsetLeft
      } else if (this.x < 0) {
        this.x = 0
      }

      this.selectedTouchEl.style.left = `${this.x}px`

      this.minValue = Math.floor((this.max * this.percentage) / 10) * 10
      this.setHighlighterPosition(this.x, null)
    }

    if (this.selectedTouchEl.matches(this.els.touchRight.selector)) {
      if (this.x < this.touchLeftEl.offsetLeft) {
        this.x = this.touchLeftEl.offsetLeft
      } else if (this.x > this.maxX) {
        this.x = this.maxX
      }
      this.selectedTouchEl.style.left = `${this.x}px`

      this.maxValue = Math.ceil((this.max * this.percentage) / 10) * 10
      this.setHighlighterPosition(
        null,
        this.els.line.element.offsetWidth - this.x,
      )
    }
  }

  setTouchElPositions () {
    const leftX = (this.minValue / this.max) * this.els.line.element.offsetWidth
    const rightX =
      (this.maxValue / this.max) * this.els.line.element.offsetWidth

    this.touchLeftEl.style.left = `${leftX}px`
    this.touchRightEl.style.left = `${rightX}px`
    this.touchRightEl.style.right = 'unset'

    let highlightPosition = this.els.line.element.offsetWidth - rightX

    if (this.els.line.element.offsetWidth === rightX) {
      highlightPosition = `0`
    }

    this.setHighlighterPosition(leftX, highlightPosition)
  }

  setHighlighterPosition (leftX, rightX) {
    if (this.els?.highlighter?.exists) {
      if (leftX !== null) {
        this.els.highlighter.element.style.left = `${leftX}px`
      }
      if (rightX !== null) {
        this.els.highlighter.element.style.right = `${rightX}px`
      }
    }
  }

  get percentage () {
    return this.x / this.els.line.element.offsetWidth
  }

  get min () {
    return this.hasAttribute('min') ? parseFloat(this.getAttribute('min')) : 0
  }

  get max () {
    return this.hasAttribute('max') ? parseFloat(this.getAttribute('max')) : 100
  }

  get maxX () {
    return this.offsetWidth - this.touchRightEl.offsetWidth * 2
  }

  set minValue (value) {
    this.setAttribute('min-value', value)

    if (this.minToUpdateEl) {
      if (Number(value) < Number(this.minToUpdateEl.min)) {
        this.minToUpdateEl.value = this.minToUpdateEl.min
      } else {
        this.minToUpdateEl.value = value
      }

      this.minToUpdateEl.dispatchEvent(new Event('input', { bubbles: true }))
    }

    this._minValue = value
  }

  get minValue () {
    if (!this._minValue) {
      return this.hasAttribute('min-value')
        ? parseFloat(this.getAttribute('min-value'))
        : this.min
    }

    return this._minValue
  }

  set maxValue (value) {
    if (this.maxToUpdateEl) {
      if (
        Number(value) === Number(this.maxToUpdateEl.max) &&
        Number(this.minToUpdateEl.value) === Number(this.minToUpdateEl.min)
      ) {
        this.maxToUpdateEl.value = ''
      } else {
        this.maxToUpdateEl.value = value
      }
      this.maxToUpdateEl.dispatchEvent(new Event('input', { bubbles: true }))
    }

    this._maxValue = value
  }

  get maxValue () {
    if (!this._maxValue) {
      return this.hasAttribute('max-value')
        ? parseFloat(this.getAttribute('max-value'))
        : this.max
    }

    return this._maxValue
  }

  get defaultMinValue () {
    const tmpMin = this.minValue >= this.min ? this.minValue : this.min
    return tmpMin > this.defaultMaxValue ? this.defaultMaxValue : tmpMin
  }

  get defaultMaxValue () {
    return this.maxValue >= this.max ? this.maxValue : this.max
  }

  get touchLeftEl () {
    return this.els.touchLeft.element
  }

  get touchRightEl () {
    return this.els.touchRight.element
  }

  get minToUpdateEl () {
    if (
      !this.externalEls?.container?.exists ||
      !this.externalEls?.min?.exists
    ) {
      return
    }
    if (this.closest(this.externalEls.container.selector)) {
      const containerEl = this.closest(this.externalEls.container.selector)
      return getElement(this.externalEls.min.selector, { context: containerEl })
    }
  }

  get maxToUpdateEl () {
    if (
      !this.externalEls?.container?.exists ||
      !this.externalEls?.max?.exists
    ) {
      return
    }
    if (this.closest(this.externalEls.container.selector)) {
      const containerEl = this.closest(this.externalEls.container.selector)
      return getElement(this.externalEls.max.selector, { context: containerEl })
    }
  }
}

if (!customElements.get('range-slider')) {
  customElements.define('range-slider', RangeSlider)
}
